"use client";

import { useCallback, useState } from "react";
import { sendError } from "@trex-2b/libs/utils/bugsnag";
import {
  BUGSNAG_APP,
  DASHBOARD_FLOWS,
} from "@trex-2b/libs/utils/bugsnag-report";
import { logGAEvent } from "@trex-2b/libs";
import { inAppWallet } from "thirdweb/wallets";
import {
  clearTrexAuth,
  initializeTrexAuth,
  setTrexAuthTokens,
  trexAuthProxy,
} from "@trex-2b/libs/utils/trex-auth-client";
import {
  thirdweb_client,
  thirdweb_trexChain,
} from "@trex-2b/libs/config/thirdweb-config";
import type { StepLoginMethod } from "@trex-2b/libs/config/login-types";
import {
  LOGIN_STRATEGY_STORAGE_KEY,
  savePassportInfo,
} from "@trex-2b/libs/utils/token-store";

const SOCIAL_PLATFORM_BY_METHOD: Record<StepLoginMethod, StepLoginMethod> = {
  google: "google",
  x: "x",
  apple: "apple",
  discord: "discord",
  telegram: "telegram",
  facebook: "facebook",
  line: "line",
};

type AuthStatus = "idle" | "loading" | "success" | "error";

type UseAuthResult = {
  status: AuthStatus;
  message: string;
  isLoading: boolean;
  login: (
    method: StepLoginMethod,
    onSuccess?: (method: StepLoginMethod) => void,
  ) => Promise<void>;
  logout: () => Promise<void>;
};

export function useAuth(): UseAuthResult {
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [message, setMessage] = useState("");

  const login = useCallback(
    async (
      method: StepLoginMethod,
      onSuccess?: (method: StepLoginMethod) => void,
    ) => {
      setStatus("loading");
      setMessage("");

      const startedAt = Date.now();
      try {
        initializeTrexAuth();
        const socialPlatform = SOCIAL_PLATFORM_BY_METHOD[method];
        const wallet = inAppWallet({
          metadata: {
            name: "trex",
            icon: "https://www.trex.xyz/icon.png?fcb9ad5f9f2e5d8f",
          },
          executionMode: {
            mode: "EIP7702",
            sponsorGas: true,
          },
        });

        await wallet.connect({
          strategy: socialPlatform,
          client: thirdweb_client,
          chain: thirdweb_trexChain,
        });

        await wallet.switchChain(thirdweb_trexChain);
        const account = wallet.getAccount();
        if (!account) {
          throw new Error("Wallet account not found");
        }

        const challengeRes = await trexAuthProxy.v1.challenge({
          address: account.address,
        });
        const challengeText = challengeRes.data?.obj?.text;
        if (!challengeText) {
          throw new Error("Challenge text is empty");
        }

        const signature = await account.signMessage({ message: challengeText });

        const loginRes = await trexAuthProxy.v1.authLogin({
          walletAddress: account.address,
          signature,
          socialPlatform,
          source: "growth",
        });
        const accessToken = loginRes.data?.obj?.accessToken;
        const refreshToken = loginRes.data?.obj?.refreshToken;
        if (!accessToken || !refreshToken) {
          throw new Error("No accessToken or refreshToken in login response");
        }

        setTrexAuthTokens({ accessToken, refreshToken });
        window.localStorage.setItem(LOGIN_STRATEGY_STORAGE_KEY, socialPlatform);

        logGAEvent("login_success", {
          method,
          social_platform: socialPlatform,
          wallet_address: account.address,
          duration_ms: Date.now() - startedAt,
        });

        const passportRes = await trexAuthProxy.v1
          .getPassportInfo()
          .catch(() => null);
        const passportInfo = passportRes?.data?.obj;
        if (passportInfo) {
          savePassportInfo(passportInfo);
        }
        onSuccess?.(method);

        setStatus("success");
        setMessage("Login success, tokens saved to cookies and localStorage.");
      } catch (error) {
        console.error("Temporary thirdweb login failed", error);
        sendError(error, {
          app: BUGSNAG_APP.GROWTH_PORTAL,
          flow: "growth_login",
          method,
          social_platform: SOCIAL_PLATFORM_BY_METHOD[method],
          duration_ms: Date.now() - startedAt,
          error_message: error instanceof Error ? error.message : String(error),
        });
        logGAEvent("login_fail", {
          method,
          social_platform: SOCIAL_PLATFORM_BY_METHOD[method],
          duration_ms: Date.now() - startedAt,
          error_type: error instanceof Error ? error.name : "UnknownError",
        });
        setStatus("error");
        setMessage(error instanceof Error ? error.message : "Login failed");
        throw error;
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    setStatus("loading");
    setMessage("");
    try {
      const wallet = inAppWallet({
        metadata: {
          name: "trex",
          icon: "https://www.trex.xyz/icon.png?fcb9ad5f9f2e5d8f",
        },
        executionMode: {
          mode: "EIP7702",
          sponsorGas: true,
        },
      });
      await wallet.disconnect().catch(() => null);
      clearTrexAuth();
      setStatus("success");
      setMessage("Logout success, tokens cleared.");
    } catch (error) {
      console.error("Temporary logout failed", error);
      sendError(error, {
        app: BUGSNAG_APP.DASHBOARD,
        flow: DASHBOARD_FLOWS.LOGOUT,
        error_message: error instanceof Error ? error.message : String(error),
      });
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Logout failed");
      throw error;
    }
  }, []);

  return {
    status,
    message,
    isLoading: status === "loading",
    login,
    logout,
  };
}
